/**
 * 管理後台 API 客戶端
 *
 * 使用 @yiweiwu/api-contract 的 EndpointDef 提供 compile-time 型別安全。
 */
import type { EndpointDef, ContractInput, ContractOutput } from '@yiweiwu/api-contract';

const BASE = '/admin-api';

// ============================================================
// 錯誤處理
// ============================================================

export class ApiError extends Error {
  code: string;
  details?: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

// ============================================================
// 核心請求
// ============================================================

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  items?: T[];
  total?: number;
  page?: number;
  pageSize?: number;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    const currentPath = window.location.pathname;
    if (currentPath !== '/admin/login') {
      window.location.href = '/admin/login';
    }
    throw new ApiError('UNAUTHORIZED', '請先登入');
  }

  const json: ApiEnvelope<T> = await res.json();

  if (!json.success) {
    throw new ApiError(
      json.error?.code ?? 'UNKNOWN',
      json.error?.message ?? '未知錯誤',
      json.error?.details,
    );
  }

  if (json.items !== undefined && json.total !== undefined) {
    return json as unknown as T;
  }

  return json.data as T;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  params?: Record<string, string>,
): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }

  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(url.toString(), options);
  return handleResponse<T>(res);
}

// ============================================================
// 便捷方法（向後相容）
// ============================================================

export function apiGet<T>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  return request<T>('GET', path, undefined, params);
}

export function apiPost<T>(
  path: string,
  body?: unknown,
): Promise<T> {
  return request<T>('POST', path, body);
}

export function apiPatch<T>(
  path: string,
  body?: unknown,
): Promise<T> {
  return request<T>('PATCH', path, body);
}

// ============================================================
// 型別安全的合約呼叫
// ============================================================

/**
 * 以合約定義呼叫 API 端點。
 *
 * 輸入/輸出型別由 EndpointDef 的 Zod schema 自動推導，
 * build 後 schema 資訊抹除，無 runtime 開銷。
 *
 * @example
 * ```ts
 * import { ordersList } from '@yiweiwu/api-contract/back-api';
 * const result = await callContract(ordersList, { page: 1 });
 * //     ^? { items: OrderListItem[], total: number, page: number, pageSize: number }
 * ```
 */
export async function callContract<T extends EndpointDef>(
  endpoint: T,
  input: ContractInput<T>,
): Promise<ContractOutput<T>> {
  return request<ContractOutput<typeof endpoint>>(
    'POST',
    endpoint.path.slice('/admin-api'.length),
    input,
  );
}
